import { paintTrack } from './trackPainter.js';
import {
  BASE_SCALE, TILE_GUTTER, TILE_LEVELS, TILE_PX,
  createDefaultCanvas, cutRowIntoTiles, releaseCanvas, tileManifest, writeBasePicture,
} from './tileCut.js';

// The tile format itself (sizes, levels, manifest) lives in tileCut.js, with the cut both exports share.
export {
  BASE_SCALE, PREVIEW_MAX_PX, TILE_GUTTER, TILE_LEVELS, TILE_PX,
  blackPicturePath, encodeWebp, releaseCanvas, tileManifest, tilePreviewPath,
} from './tileCut.js';

// How much of the map is painted per call. 512 world units is one level-1 tile row, and two level-0
// rows; the canvas of a 7900-unit-wide track is ~16 Mpx (65 MB), which a browser handles.
const STRIP_WORLD = 512;
// Extra world units painted above and below a strip: the gutter of the COARSEST level is the widest
// in world units, and it has to find painted content even at a strip boundary.
const STRIP_MARGIN = Math.ceil(TILE_GUTTER / TILE_LEVELS[TILE_LEVELS.length - 1]);

/**
 * Exports a track's look as a TILE PYRAMID: the same picture the game used to receive whole, cut
 * into small tiles at several densities, plus a small whole-map fallback. The game draws only the
 * tiles a camera is looking at, at the level closest to the screen's own density (see the game's
 * rendering/TrackTiles.js); this is what makes a track sharp at every zoom without holding a
 * photograph of the whole map in memory.
 *
 * The painting is done in STRIPS, never on one canvas of the whole map: the painter draws in world
 * coordinates on a context the caller has scaled and translated, so a strip of the map painted on
 * its own canvas is pixel-for-pixel the same as the same strip of a full-map painting (verified
 * against a full paint at the same scale). One strip of the biggest tracks is ~65 MB, against the
 * 500+ MB a 2 px/unit canvas of the whole map would need.
 *
 * @param {object} track                the track file, as the game reads it
 * @param {object} opts
 * @param {Function} opts.encode        `(canvas, quality) => Promise<Uint8Array|Blob>`, WebP
 * @param {Function} [opts.createCanvas] `(w, h) => canvas`; defaults to document.createElement
 * @param {object} [opts.images]        decoded material sprites, keyed by src (see trackPainter)
 * @param {object} [opts.terrainImages] decoded ground textures, keyed by terrain
 * @param {Function} [opts.onProgress]  `({ phase, index, total })`; may return a promise (a paint is
 *                                      synchronous, so this is where a caller lets the browser repaint)
 * @returns {Promise<{ manifest, files: { path: string, data: Uint8Array }[] }|null>}
 */
export async function exportTrackTiles(track, {
  encode,
  createCanvas = createDefaultCanvas,
  images = {},
  terrainImages = {},
  onProgress = null,
} = {}) {
  if (!track || !(track?.width > 0) || !(track?.height > 0)) return null;
  if (typeof encode !== 'function') throw new Error('exportTrackTiles: an encode(canvas) function is required');

  const id = track.id ?? 'track_custom';
  const files = [];
  let tileCount = 0;
  const stripCount = Math.ceil(track.height / STRIP_WORLD);

  // The whole map at BASE_SCALE: transparent where the map ends, stretched over the world by the
  // game. It is the fallback under every tile, never a hole while a tile is in flight.
  const base = createCanvas(
    Math.max(1, Math.ceil(track.width * BASE_SCALE)),
    Math.max(1, Math.ceil(track.height * BASE_SCALE)),
  );
  const baseCtx = base.getContext('2d');
  baseCtx.imageSmoothingEnabled = true;
  baseCtx.imageSmoothingQuality = 'high';

  // One accumulator per level: a tile row is `TILE_PX / scale` world units tall, which is not the
  // same as a strip for every level, so a row can span several strips (or several rows fit in one).
  const rows = TILE_LEVELS.map((scale, level) => ({
    level,
    scale,
    rowWorld: TILE_PX / scale,
    canvas: null,
    ctx: null,
    startY: 0,
    filled: 0,
  }));

  const flushRow = async (row) => {
    if (!row.canvas) return;
    const rowIndex = Math.round(row.startY / row.rowWorld);
    tileCount += await cutRowIntoTiles({
      canvas: row.canvas, level: row.level, rowIndex, id, createCanvas, encode, files,
    });
    releaseCanvas(row.canvas);
    row.canvas = null;
    row.ctx = null;
    row.filled = 0;
  };

  const addStripToRow = async (row, strip, stripY, stripHeight) => {
    let localY = 0;
    // A texel of gutter, in world units at this level. Above and below the row it comes from the
    // strip's own painted margin; left and right it is already in the padded row canvas.
    const gutterWorld = TILE_GUTTER / row.scale;
    while (localY < stripHeight - 1e-6) {
      const worldY = stripY + localY;
      const startY = Math.floor(worldY / row.rowWorld) * row.rowWorld;
      if (!row.canvas || row.startY !== startY) {
        if (row.canvas) await flushRow(row);
        row.canvas = createCanvas(
          Math.max(1, Math.ceil(track.width * row.scale)) + TILE_GUTTER * 2,
          TILE_PX + TILE_GUTTER * 2,
        );
        row.ctx = row.canvas.getContext('2d');
        row.ctx.imageSmoothingEnabled = true;
        row.ctx.imageSmoothingQuality = 'high';
        row.startY = startY;
        row.filled = 0;
      }
      const offset = worldY - row.startY;
      const take = Math.min(stripHeight - localY, row.rowWorld - offset);
      // Source rect is in the strip's pixels (2 px/unit, plus STRIP_MARGIN around it); destination
      // is the row's own density. drawImage does the downscale the level asks for.
      row.ctx.drawImage(
        strip,
        0, (localY + STRIP_MARGIN) * 2, strip.width, take * 2,
        TILE_GUTTER, TILE_GUTTER + offset * row.scale, track.width * row.scale, take * row.scale,
      );
      // The row's first and last texel of gutter, read from the painted margin above/below it.
      if (offset <= 1e-6) {
        row.ctx.drawImage(
          strip,
          0, (localY + STRIP_MARGIN - gutterWorld) * 2, strip.width, gutterWorld * 2,
          TILE_GUTTER, 0, track.width * row.scale, TILE_GUTTER,
        );
      }
      if (offset + take >= row.rowWorld - 1e-6) {
        row.ctx.drawImage(
          strip,
          0, (localY + take + STRIP_MARGIN) * 2, strip.width, gutterWorld * 2,
          TILE_GUTTER, TILE_GUTTER + TILE_PX, track.width * row.scale, TILE_GUTTER,
        );
      }
      row.filled += take;
      localY += take;
      if (row.filled >= row.rowWorld - 1e-6) await flushRow(row);
    }
  };

  for (let index = 0; index < stripCount; index++) {
    const stripY = index * STRIP_WORLD;
    const stripHeight = Math.min(STRIP_WORLD, track.height - stripY);
    await onProgress?.({ phase: 'paint', index, total: stripCount });

    const strip = createCanvas(
      Math.max(1, Math.ceil(track.width * 2)),
      Math.max(1, Math.ceil((stripHeight + STRIP_MARGIN * 2) * 2)),
    );
    const ctx = strip.getContext('2d');
    ctx.scale(2, 2);
    ctx.translate(0, -(stripY - STRIP_MARGIN));
    ctx.beginPath();
    ctx.rect(0, stripY - STRIP_MARGIN, track.width, stripHeight + STRIP_MARGIN * 2);
    ctx.clip();
    paintTrack(ctx, track, {
      scale: 2, createCanvas, images, terrainImages,
      bounds: { x: 0, y: stripY - STRIP_MARGIN, width: track.width, height: stripHeight + STRIP_MARGIN * 2 },
    });

    // Only the strip's own band goes into the base: its painted margin belongs to the neighbours.
    baseCtx.drawImage(
      strip,
      0, STRIP_MARGIN * 2, strip.width, stripHeight * 2,
      0, stripY * BASE_SCALE, track.width * BASE_SCALE, stripHeight * BASE_SCALE,
    );
    for (const row of rows) await addStripToRow(row, strip, stripY, stripHeight);
    releaseCanvas(strip);   // ~65 MB: the next strip must not be allocated on top of it
  }
  for (const row of rows) await flushRow(row);

  await writeBasePicture({ base, track, createCanvas, encode, files });
  releaseCanvas(base);

  return { manifest: tileManifest(track), files, tileCount };
}
